import { Leakage } from "01Data/01Admin/Devices";
import { AppContext } from "01Data/AppData";
import PageTemplate from "03AdminPages/00Templates/PageTemplate";
import { useModel } from "99Tools/useModel";
import { FormatDate, FormatDateISO } from "99Tools/utils";
import { Article, OpenInNew } from "@mui/icons-material";
import { Backdrop, Button, CircularProgress, styled } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router";
import { LIST_HEADER_COLOR } from "Settings";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

/**
 * カラム設定
 */
const columns = [
  {
    id: "date",
    label: "発生日時",
    minWidthPC: 150,
    minWidthSmartphone: "18vw",
  },
  {
    id: "id",
    label: "デバイス",
    minWidthPC: 150,
    minWidthSmartphone: "18vw",
  },
  {
    id: "user",
    label: "管理者",
    minWidthPC: 150,
    minWidthSmartphone: "18vw",
  },
  { id: "map", label: "詳細場所", minWidthPC: 50, minWidthSmartphone: "8vw" },
  { id: "showLog", label: "", minWidthPC: 50, minWidthSmartphone: "8vw" },
];

/**
 * 漏水一覧
 * @returns TSX
 */
const UserBody = () => {
  const a = useContext(AppContext).dataAdmin;
  const list = a.devices;
  const navigation = useNavigate();

  const handleOpenLog = (i: number) => {
    a.log.Clear();
    navigation(
      `/admin/log/leakage` +
        `?id=${list.leakages[i].id}` +
        `&last=${FormatDateISO(list.leakages[i].LastLeaked).split("T")[0]}`
    );
    list.changed();
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const rows = list.leakages
    .sort((a, b) => b.LastLeaked.localeCompare(a.LastLeaked))
    .slice(
      list.pageLeakage * list.rowsPerPageLeakage,
      list.pageLeakage * list.rowsPerPageLeakage + list.rowsPerPageLeakage
    )
    .map((row: Leakage, i: number) => {
      const index = list.pageLeakage * list.rowsPerPageLeakage + i;
      return (
        <TableRow
          hover
          role="checkbox"
          tabIndex={-1}
          key={row.LastLeaked + row.id}
        >
          <TableCell key={columns[0].id + i} align="center">
            {FormatDate(row.LastLeaked)}
          </TableCell>
          <TableCell key={columns[1].id + i} align="center">
            {row.id}
          </TableCell>
          <TableCell key={columns[2].id + i} align="center">
            {row.user.UserName}
            <br />({row.user.Email})
          </TableCell>
          <TableCell key={columns[3].id + i} align="center">
            {row.ImageURL === "" ? (
              <Button
                size="small"
                endIcon={<OpenInNew />}
                variant="contained"
                disabled
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            ) : (
              <Button
                size="small"
                endIcon={<OpenInNew />}
                variant="contained"
                onClick={() => handleShowMap(row.ImageURL)}
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            )}
          </TableCell>
          <TableCell key={columns[4].id + i} align="center">
            <Button
              size="small"
              startIcon={<Article />}
              variant="contained"
              onClick={() => handleOpenLog(index)}
            >
              ログ表示
            </Button>
          </TableCell>
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

/**
 * ユーザテーブル
 * @returns TSX
 */
const UserTable = () => {
  const list = useContext(AppContext).dataAdmin.devices;
  useModel(list);
  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    list.pageLeakage = newPage;
    list.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    list.rowsPerPageLeakage = +event.target.value;
    list.pageLeakage = 0;
    list.changed();
  };

  const header = columns.map((column) => (
    <ColorHeader
      key={column.id}
      align="center"
      style={{
        minWidth: isSmartphone ? column.minWidthSmartphone : column.minWidthPC,
      }}
    >
      {column.label}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "60vh" }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <UserBody />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={list.leakages.length}
        rowsPerPage={list.rowsPerPageLeakage}
        page={list.pageLeakage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

/**
 * 漏水一覧ページ
 * @returns TSX
 */
const LeakagePage = () => {
  const d = useContext(AppContext).dataAdmin;
  const list = d.devices;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    d.state = "autoUpdate";
    list.Update().finally(() => setLoading(false));
    return () => {
      d.state = "normal";
    };
  }, [d, list]);

  return (
    <PageTemplate title="漏水発生">
      {UserTable()}
      <Backdrop open={loading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </PageTemplate>
  );
};

export default LeakagePage;
